import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  PlayArrow,
  Refresh,
  Save,
  ContentCopy,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { EditorView } from '@components/BlockNote/EditorView';
import { AppHeader } from '../components/app-header';
import {
  useStartModelConversionMutation,
  useModelConversionStatusQuery,
  useSaveModelConversionMutation,
  useCopyModelConversionMutation,
} from '../store/hooks';

type ConversionPhase = 'initializing' | 'converting' | 'validating' | 'completed' | 'error';

const PHASE_LABELS: Record<string, string> = {
  initializing: 'Initializing',
  converting: 'Converting Prompts',
  validating: 'Validating Results',
  completed: 'Complete',
  error: 'Error',
};

export default function TemplateModelConversion() {
  const { enqueueSnackbar } = useSnackbar();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    note_template_id: '',
    target_model: 'gpt-5-mini',
    runs_per_example: 1,
    max_validation_examples: 1,
    skip_validation: false,
  });
  const [selectedTab, setSelectedTab] = useState<{ [key: string]: number }>({});
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [saveFormData, setSaveFormData] = useState({
    target_user_id: '',
    title: '',
    description: '',
  });
  const [copyFormData, setCopyFormData] = useState({
    target_user_id: '',
    target_organization_id: '',
    owner_scope: 'user' as 'user' | 'organization',
    title: '',
    description: '',
  });

  // Start conversion mutation
  const [startConversion, { isLoading: isStarting }] = useStartModelConversionMutation();

  // Save and copy mutations
  const [saveConversion, { isLoading: isSaving }] = useSaveModelConversionMutation();
  const [copyConversion, { isLoading: isCopying }] = useCopyModelConversionMutation();

  // Poll status query
  const {
    data: statusData,
    isLoading: isLoadingStatus,
    error: statusError,
  } = useModelConversionStatusQuery(
    { threadId: threadId || '' },
    {
      skip: !threadId,
      pollingInterval: isPolling ? 2000 : 0,
    },
  );

  // Control polling based on status
  useEffect(() => {
    if (threadId) {
      // Start polling when we have a thread ID
      setIsPolling(true);
    }
  }, [threadId]);

  // Stop polling when completed or error
  useEffect(() => {
    if (statusData?.status === 'completed' || statusData?.status === 'error') {
      setIsPolling(false);
    }
  }, [statusData?.status]);

  const handleStartConversion = async () => {
    try {
      const result = await startConversion({
        startModelConversionRequest: {
          user_id: formData.user_id,
          note_template_id: formData.note_template_id,
          target_model: formData.target_model || undefined,
          runs_per_example: formData.runs_per_example || undefined,
          max_validation_examples: formData.max_validation_examples || undefined,
          skip_validation: formData.skip_validation || undefined,
        },
      }).unwrap();
      setThreadId(result.thread_id);
    } catch (error) {
      console.error('Failed to start conversion:', error);
    }
  };

  const handleTabChange = (sectionKey: string, newValue: number) => {
    setSelectedTab(prev => ({ ...prev, [sectionKey]: newValue }));
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  const currentPhase = (statusData?.current_phase || 'initializing') as ConversionPhase;
  const isInProgress = threadId && statusData && statusData.status !== 'completed' && statusData.status !== 'error';

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Template Model Conversion
          </h1>

          {/* Start Form */}
          {!threadId && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Start Conversion
                </Typography>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <TextField
                    label="User ID"
                    value={formData.user_id}
                    onChange={e => setFormData({ ...formData, user_id: e.target.value })}
                    required
                    fullWidth
                    helperText="User ID to find example notes for"
                  />
                  <TextField
                    label="Note Template ID"
                    value={formData.note_template_id}
                    onChange={e => setFormData({ ...formData, note_template_id: e.target.value })}
                    required
                    fullWidth
                    helperText="Note template UUID (new format)"
                  />
                  <TextField
                    label="Target Model"
                    value={formData.target_model}
                    onChange={e => setFormData({ ...formData, target_model: e.target.value })}
                    fullWidth
                    helperText="Target model to optimize for (default: gpt-5-mini)"
                  />
                  <TextField
                    label="Runs Per Example"
                    type="number"
                    value={formData.runs_per_example}
                    onChange={e => setFormData({ ...formData, runs_per_example: parseInt(e.target.value) || 1 })}
                    fullWidth
                    inputProps={{ min: 1, max: 10 }}
                    helperText="Number of validation generations to run per example (default: 1)"
                  />
                  <TextField
                    label="Max Validation Examples"
                    type="number"
                    value={formData.max_validation_examples}
                    onChange={e => setFormData({ ...formData, max_validation_examples: parseInt(e.target.value) || 1 })}
                    fullWidth
                    inputProps={{ min: 1, max: 10 }}
                    helperText="Maximum examples per section to validate (default: 1)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.skip_validation}
                        onChange={e => setFormData({ ...formData, skip_validation: e.target.checked })}
                      />
                    }
                    label="Skip validation phase (faster, but no comparison data)"
                  />
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={handleStartConversion}
                    disabled={isStarting || !formData.user_id || !formData.note_template_id}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {isStarting ? 'Starting...' : 'Start Conversion'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Progress Display */}
          {isInProgress && statusData && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="h6">Conversion Progress</Typography>
                  <Chip
                    label={PHASE_LABELS[currentPhase] || currentPhase}
                    color={currentPhase === 'error' ? 'error' : currentPhase === 'completed' ? 'success' : 'primary'}
                    size="small"
                  />
                </Box>
                
                {/* Progress Bar */}
                {currentPhase === 'converting' ? (
                  // Indeterminate progress during conversion
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="indeterminate"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {statusData.progress_message || 'Converting prompts...'}
                      {statusData.sections_converted !== null && statusData.sections_converted !== undefined && (
                        <span> ({statusData.sections_converted} sections)</span>
                      )}
                    </Typography>
                  </Box>
                ) : statusData.progress_percent !== undefined ? (
                  // Determinate progress for other phases
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={statusData.progress_percent}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {statusData.progress_percent}%
                    </Typography>
                  </Box>
                ) : (
                  // Indeterminate for other phases without percent
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="indeterminate"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                )}

                {/* Progress Message */}
                {statusData.progress_message && currentPhase !== 'converting' && (
                  <Typography variant="body2" color="text.secondary">
                    {statusData.progress_message}
                  </Typography>
                )}

                {/* Current Step */}
                {statusData.current_step && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {statusData.current_step}
                  </Typography>
                )}

                {/* Sections Converted (shown after conversion phase) */}
                {currentPhase !== 'converting' && statusData.sections_converted !== null && statusData.sections_converted !== undefined && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sections converted: {statusData.sections_converted}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {statusData?.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Error:</strong> {statusData.error}
              </Typography>
            </Alert>
          )}

          {/* Results Display */}
          {statusData?.status === 'completed' && (
            <Box>
              {/* Conversion Results */}
              {statusData.conversion_results && statusData.conversion_results.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Conversion Results ({statusData.conversion_results.length} sections)
                    </Typography>
                    {statusData.conversion_results.map((result: any, index: number) => (
                      <Accordion key={result.section_id || index} defaultExpanded={index === 0}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Typography variant="subtitle1">
                              {result.section_title || result.section_id || `Section ${index + 1}`}
                            </Typography>
                            {result.examples_used && (
                              <Chip
                                label={`${result.examples_used} example${result.examples_used !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Original vs Converted Prompt */}
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Prompt Comparison
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', mt: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
                                    Original Prompt (legacy GPT-4o)
                                  </Typography>
                                  <Box
                                    sx={{
                                      border: 1,
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      p: 2,
                                      bgcolor: 'grey.50',
                                      fontFamily: 'monospace',
                                      fontSize: '0.875rem',
                                      whiteSpace: 'pre-wrap',
                                      maxHeight: 300,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {result.original_prompt || 'N/A'}
                                  </Box>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
                                    Converted Prompt (optimized for {formData.target_model || 'GPT-5-mini'})
                                  </Typography>
                                  <Box
                                    sx={{
                                      border: 1,
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      p: 2,
                                      bgcolor: 'grey.50',
                                      fontFamily: 'monospace',
                                      fontSize: '0.875rem',
                                      whiteSpace: 'pre-wrap',
                                      maxHeight: 300,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {result.converted_prompt || 'N/A'}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>

                            {/* Conversion Reasoning */}
                            {result.conversion_reasoning && (
                              <Box>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
                                  Conversion Reasoning
                                </Typography>
                                <Box
                                  sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {result.conversion_reasoning}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Validation Results */}
              {statusData.validation_results && statusData.validation_results.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Validation Results
                    </Typography>
                    {statusData.validation_results.map((section: any, sectionIndex: number) => (
                      <Accordion key={section.section_id || sectionIndex} defaultExpanded={sectionIndex === 0} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Typography variant="subtitle1">
                              {section.section_title || section.section_id || `Section ${sectionIndex + 1}`}
                            </Typography>
                            {section.success ? (
                              <Chip icon={<CheckCircle />} label="Success" color="success" size="small" />
                            ) : (
                              <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                            )}
                            {section.total_elapsed_seconds && (
                              <Chip
                                label={formatDuration(section.total_elapsed_seconds)}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {section.error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              {section.error}
                            </Alert>
                          )}

                          {/* Examples with side-by-side comparison */}
                          {section.examples && section.examples.map((example: any, exampleIndex: number) => {
                            const sectionKey = `${section.section_id}-${example.example_index}`;
                            const currentTab = selectedTab[sectionKey] ?? 0;

                            return (
                              <Accordion key={example.example_index} defaultExpanded={exampleIndex === 0} sx={{ mb: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Box display="flex" alignItems="center" gap={2} width="100%">
                                    <Typography variant="subtitle2">
                                      Example {example.example_index + 1}
                                    </Typography>
                                    <Chip
                                      label={`Note: ${example.note_id?.substring(0, 8)}...`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                                    />
                                    {example.generations && (
                                      <Chip
                                        label={`${example.generations.length} generation${example.generations.length !== 1 ? 's' : ''}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box>
                                    {/* Example Output Comparison Header */}
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                                      Example Output Comparison
                                    </Typography>

                                    {/* Tabs above right column */}
                                    {example.generations && example.generations.length > 0 && (
                                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Tabs
                                          value={currentTab}
                                          onChange={(_, newValue) => handleTabChange(sectionKey, newValue)}
                                          sx={{ borderBottom: 1, borderColor: 'divider' }}
                                        >
                                          {example.generations.map((gen: any, genIndex: number) => (
                                            <Tab
                                              key={genIndex}
                                              label={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                  <span>Run {gen.run_index + 1}</span>
                                                  {gen.error && (
                                                    <ErrorIcon color="error" sx={{ fontSize: '1rem' }} />
                                                  )}
                                                  {!gen.error && gen.elapsed_seconds && (
                                                    <Chip
                                                      label={formatDuration(gen.elapsed_seconds)}
                                                      size="small"
                                                      sx={{ height: '18px', fontSize: '0.65rem' }}
                                                    />
                                                  )}
                                                </Box>
                                              }
                                            />
                                          ))}
                                        </Tabs>
                                      </Box>
                                    )}

                                    {/* Side by side layout */}
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'stretch',
                                      }}
                                    >
                                      {/* Left side - Original Approved (always visible) */}
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          border: 1,
                                          borderColor: 'divider',
                                          borderRadius: 1,
                                          p: 2,
                                          bgcolor: 'grey.50',
                                          minHeight: 0,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          fontWeight="bold"
                                          color="text.secondary"
                                          gutterBottom
                                          display="block"
                                          sx={{ mb: 1, height: '20px', display: 'flex', alignItems: 'center' }}
                                        >
                                          User Approved
                                        </Typography>
                                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                          <EditorView
                                            markdown={example.original_approved || ''}
                                            editable={false}
                                            sideMenu={false}
                                            formattingToolbar={false}
                                          />
                                        </Box>
                                      </Box>

                                      {/* Right side - Generations */}
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          minHeight: 0,
                                        }}
                                      >
                                        {example.generations && example.generations.length > 0 ? (
                                          (() => {
                                            const gen = example.generations[currentTab];
                                            if (!gen) {
                                              return null;
                                            }
                                            if (gen.error) {
                                              return (
                                                <Alert severity="error">
                                                  <Typography variant="body2">
                                                    <strong>Error:</strong> {gen.error}
                                                  </Typography>
                                                </Alert>
                                              );
                                            }
                                            return (
                                              <Box
                                                sx={{
                                                  flex: 1,
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  border: 1,
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  p: 2,
                                                  bgcolor: 'grey.50',
                                                  minHeight: 0,
                                                }}
                                              >
                                                <Box
                                                  display="flex"
                                                  justifyContent="space-between"
                                                  alignItems="center"
                                                  mb={1}
                                                  sx={{ height: '20px', display: 'flex', alignItems: 'center' }}
                                                >
                                                  <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text.secondary"
                                                  >
                                                    New Generation (Run {gen.run_index + 1})
                                                  </Typography>
                                                  <Box display="flex" gap={1} alignItems="center">
                                                    {gen.model_used && (
                                                      <Chip
                                                        label={gen.model_used}
                                                        size="small"
                                                        variant="outlined"
                                                      />
                                                    )}
                                                    {gen.elapsed_seconds && (
                                                      <Chip
                                                        label={formatDuration(gen.elapsed_seconds)}
                                                        size="small"
                                                        variant="outlined"
                                                      />
                                                    )}
                                                  </Box>
                                                </Box>
                                                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                                  <EditorView
                                                    markdown={gen.new_generation || ''}
                                                    editable={false}
                                                    sideMenu={false}
                                                    formattingToolbar={false}
                                                  />
                                                </Box>
                                              </Box>
                                            );
                                          })()
                                        ) : (
                                          <Alert severity="info">
                                            No generations available for this example
                                          </Alert>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => {
                      setSaveFormData({
                        target_user_id: formData.user_id,
                        title: `Converted to ${formData.target_model}`,
                        description: `Template converted from legacy format to ${formData.target_model}`,
                      });
                      setSaveDialogOpen(true);
                    }}
                    disabled={!statusData?.proposed_revision}
                  >
                    Save as New Revision
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={() => {
                      setCopyFormData({
                        target_user_id: formData.user_id,
                        target_organization_id: '',
                        owner_scope: 'user',
                        title: `${statusData?.proposed_revision?.title || 'Template'} (Copy)`,
                        description: `Copy of template converted to ${formData.target_model}`,
                      });
                      setCopyDialogOpen(true);
                    }}
                    disabled={!statusData?.proposed_revision}
                  >
                    Copy to New Template
                  </Button>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => {
                    setThreadId(null);
                    setSelectedTab({});
                  }}
                >
                  Start New Conversion
                </Button>
              </Box>

              {/* Save as New Revision Dialog */}
              <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Save as New Revision</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                      label="Target User ID"
                      value={saveFormData.target_user_id}
                      onChange={e => setSaveFormData({ ...saveFormData, target_user_id: e.target.value })}
                      required
                      fullWidth
                      helperText="User ID creating the revision"
                    />
                    <TextField
                      label="Revision Title"
                      value={saveFormData.title}
                      onChange={e => setSaveFormData({ ...saveFormData, title: e.target.value })}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Description"
                      value={saveFormData.description}
                      onChange={e => setSaveFormData({ ...saveFormData, description: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!statusData?.proposed_revision || !statusData?.note_template_id) return;
                      try {
                        const result = await saveConversion({
                          saveConvertedRevisionRequest: {
                            note_template_id: statusData.note_template_id,
                            target_user_id: saveFormData.target_user_id,
                            title: saveFormData.title,
                            description: saveFormData.description || undefined,
                            sections: statusData.proposed_revision.sections || [],
                            content: statusData.proposed_revision.content,
                            metadata: statusData.proposed_revision.metadata,
                            models: statusData.proposed_revision.models,
                          },
                        }).unwrap();
                        setSaveDialogOpen(false);
                        enqueueSnackbar(
                          result.message || `Successfully saved as revision ${result.new_version}`,
                          { variant: 'success' }
                        );
                      } catch (error: any) {
                        console.error('Failed to save revision:', error);
                        enqueueSnackbar(
                          error?.data?.message || 'Failed to save revision',
                          { variant: 'error' }
                        );
                      }
                    }}
                    disabled={isSaving || !saveFormData.target_user_id || !saveFormData.title}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Copy to New Template Dialog */}
              <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Copy to New Template</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                      label="Target User ID"
                      value={copyFormData.target_user_id}
                      onChange={e => setCopyFormData({ ...copyFormData, target_user_id: e.target.value })}
                      required
                      fullWidth
                      helperText="User ID to own the new template"
                    />
                    <TextField
                      label="Target Organization ID"
                      value={copyFormData.target_organization_id}
                      onChange={e => setCopyFormData({ ...copyFormData, target_organization_id: e.target.value })}
                      fullWidth
                      helperText="Optional - auto-looked up from user if not provided"
                    />
                    <FormControl fullWidth>
                      <InputLabel>Owner Scope</InputLabel>
                      <Select
                        value={copyFormData.owner_scope}
                        label="Owner Scope"
                        onChange={e => setCopyFormData({ ...copyFormData, owner_scope: e.target.value as 'user' | 'organization' })}
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="organization">Organization</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Template Title"
                      value={copyFormData.title}
                      onChange={e => setCopyFormData({ ...copyFormData, title: e.target.value })}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Description"
                      value={copyFormData.description}
                      onChange={e => setCopyFormData({ ...copyFormData, description: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!statusData?.proposed_revision || !statusData?.note_template_id) return;
                      try {
                        const result = await copyConversion({
                          copyToNewTemplateRequest: {
                            source_template_id: statusData.note_template_id,
                            target_user_id: copyFormData.target_user_id,
                            target_organization_id: copyFormData.target_organization_id || undefined,
                            owner_scope: copyFormData.owner_scope,
                            title: copyFormData.title,
                            description: copyFormData.description || undefined,
                            sections: statusData.proposed_revision.sections || [],
                            content: statusData.proposed_revision.content,
                            metadata: statusData.proposed_revision.metadata,
                            models: statusData.proposed_revision.models,
                          },
                        }).unwrap();
                        setCopyDialogOpen(false);
                        enqueueSnackbar(
                          result.message || `Successfully created new template: ${result.new_template_id}`,
                          { variant: 'success' }
                        );
                      } catch (error: any) {
                        console.error('Failed to copy template:', error);
                        enqueueSnackbar(
                          error?.data?.message || 'Failed to copy template',
                          { variant: 'error' }
                        );
                      }
                    }}
                    disabled={isCopying || !copyFormData.target_user_id || !copyFormData.title}
                  >
                    {isCopying ? 'Copying...' : 'Copy'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
        </div>
      </main>
    </div>
  );
}

